'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Icon } from '#pplx/frontend/aether/components/Icon/index.js';
import { Spinner } from '#pplx/frontend/aether/components/Spinner/index.js';
import { BoxPrimitive } from '#pplx/frontend/aether/primitives/BoxPrimitive/index.js';
import { TextPrimitive } from '#pplx/frontend/aether/primitives/TextPrimitive/index.js';
import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import { usePrevious } from '#pplx/frontend/common/hooks/usePrevious.js';
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import Button from '#pplx/frontend/components/buttons/Button.js';
import TextButton from '#pplx/frontend/components/buttons/TextButton.js';
import { FormItemHeader } from '#pplx/frontend/components/form/FormItemHeader.js';
import { handleCompositionStart, handleCompositionEnd, InputErrorPlacement, AUTOCOMPLETE_TYPES, } from '#pplx/frontend/components/form/InputShared.js';
import { Size } from '#pplx/frontend/components/types/types.js';
import { handleKeyDownInput } from '#pplx/frontend/composer-sdk/keyboard.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import classNames from 'classnames';
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, } from 'react';
const ClearInputComponent = ({ onClear }) => {
    return _jsx(TextButton, { icon: $icon('x'), pill: true, onClick: onClear, size: Size.small });
};
/**
 * @deprecated - Use `aether/components/Input` instead. Reach out to #web-design-system for help.
 */
const Input = React.memo(function Input({ type, className, wrapperClassName, value, name, maxLength, errorText, errorPlacement = InputErrorPlacement.INLINE, disabled = false, required = false, autoFocus = false, placeholder = '', rightItems = EMPTY_ARRAY, onChange, onClear, onBlur, onFocus, onPaste, onKeyDown, isLoading, inlineEditBlock, label, subtitle, isOptional, isMobileUserAgent, labelMargin = true, testId, variant, disable1pass, ClearIcon = ClearInputComponent, enterKeyHint, size, ref, colorVariant = 'default', min, max, step, spellCheck, leadingAccessory, ariaLabel, }) {
    const prevDisabled = usePrevious(disabled);
    const inputRef = useRef(null);
    const [isActive, setIsActive] = useState(false);
    const [isComposing, setIsComposing] = useState(false);
    const isActiveRef = useRef(isActive);
    isActiveRef.current = isActive;
    const hasLeadingAccessory = !!leadingAccessory || type === 'search';
    useEffect(() => {
        if (!isMobileUserAgent && autoFocus) {
            inputRef.current?.focus();
        }
    }, [isMobileUserAgent, inputRef, autoFocus]);
    useEffect(() => {
        if (prevDisabled !== disabled && !disabled && !isMobileUserAgent && autoFocus) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 200);
        }
    });
    const clearWrapper = useMemo(() => classNames({
        'border-r mr-2 pr-1': rightItems.length != 0,
    }), [rightItems.length]);
    const inputClass = useMemo(() => {
        const bgColorClass = (() => {
            switch (colorVariant) {
                case 'default':
                    return 'bg-raised';
                case 'subtle':
                    return 'bg-soft';
                default:
                    assertUnreachableValue(colorVariant);
            }
        })();
        const commonClasses = classNames('w-full outline-hidden focus:outline-hidden focus:ring-base font-sans flex items-center', 'text-primary selection:bg-super-selection dark:selection:text-super-primary', bgColorClass, 'border focus:ring-1', 'duration-200 transition-all', 'rounded-lg', {
            'ring-base ring-1': isActive,
        }, className);
        const sizeClasses = classNames({
            // Extra small size
            'py-1 px-2 text-xs placeholder:text-xs': size === 'xs',
            // Small size
            'py-2 px-2 text-sm placeholder:text-sm': size === 'sm',
            // Large size
            'py-8 px-12 text-lg placeholder:text-base': size === 'lg',
            // Default size (matches current padding)
            'pl-9': hasLeadingAccessory,
            '!font-mono md:text-base': variant === 'monospace',
            '!text-3xl !font-medium': variant === 'page-title',
            'py-2 md:text-sm px-4 placeholder:text-sm': !size && !variant,
        });
        const rightItemsClasses = classNames({
            'pr-1': rightItems.length === 0 && size === 'xs',
            'pr-2': rightItems.length === 0 && size === 'sm',
            'pr-4': rightItems.length === 0 && !size,
            'pr-8': rightItems.length === 0 && size === 'lg',
            'pr-[49px] md:pr-[59px]': rightItems.length === 1,
            'pr-[128px] md:pr-[138px]': rightItems.length === 2,
        });
        return classNames(commonClasses, sizeClasses, rightItemsClasses);
    }, [isActive, className, size, variant, rightItems.length, colorVariant, hasLeadingAccessory]);
    const disabledErrorMessage = !!errorText;
    const disabledMaxLength = !disabledErrorMessage && !!maxLength && !!value && value.length > maxLength;
    const leadingAccessoryComponent = useMemo(() => {
        if (!hasLeadingAccessory) {
            return null;
        }
        return (_jsx("div", { className: "absolute left-3 flex items-center", children: _jsx(TextPrimitive, { color: "light", className: "inline-flex", children: _jsx(Icon, { icon: leadingAccessory || $icon('search'), size: "small" }) }) }));
    }, [leadingAccessory, hasLeadingAccessory]);
    const rightAttribution = useMemo(() => {
        const shouldShowInlineErrorMessage = errorPlacement === InputErrorPlacement.INLINE && disabledErrorMessage;
        return (_jsxs("div", { className: "right-2 gap-2 absolute flex items-center rounded-full", children: [isLoading ? (_jsx(TextPrimitive, { color: "light", className: "mr-1", children: _jsx(Spinner, {}) })) : null, shouldShowInlineErrorMessage && (_jsx(BoxPrimitive, { className: "mr-2", children: _jsx(TextPrimitive, { color: "caution", variant: "tiny", children: errorText }) })), disabledMaxLength && (_jsx(BoxPrimitive, { className: "mr-2", children: maxLength && value && (_jsx(TextPrimitive, { color: "caution", variant: "tiny", children: maxLength - value.length })) })), onClear && (_jsx("div", { className: clearWrapper, children: _jsx(ClearIcon, { onClear: onClear, inputValue: value }) })), rightItems.map((item, i) => (_jsx(Button, { ...item.buttonProps, size: item.buttonProps.size, disabled: item.buttonProps.disabled || disabledErrorMessage || disabledMaxLength, pill: true }, i)))] }));
    }, [
        errorPlacement,
        disabledErrorMessage,
        isLoading,
        errorText,
        disabledMaxLength,
        maxLength,
        value,
        onClear,
        clearWrapper,
        ClearIcon,
        rightItems,
    ]);
    const focusAtEnd = useCallback(() => {
        if (inputRef.current) {
            const len = inputRef.current.value.length;
            inputRef.current.focus();
            try {
                inputRef.current.setSelectionRange(len, len);
            }
            catch {
                // do nothing. not all input types support range selection
            }
        }
    }, []);
    const handleFocus = useCallback((e) => {
        onFocus?.(e.nativeEvent);
        setIsActive(true);
    }, [onFocus]);
    const handleBlur = useCallback((e) => {
        onBlur?.(e.nativeEvent);
        setIsActive(false);
    }, [onBlur]);
    useImperativeHandle(ref, () => {
        // Here we need to assign these extra APIs as you can't spread DOM Node
        const extendedRef = inputRef.current;
        extendedRef.focusAtEnd = focusAtEnd;
        extendedRef.isFocused = () => isActiveRef.current;
        extendedRef.inLine = () => true; // input is single line
        extendedRef.append = (value) => {
            if (inputRef.current) {
                inputRef.current.value += value;
            }
        };
        extendedRef.scrollToEnd = () => {
            if (inputRef.current) {
                inputRef.current.scrollTop = inputRef.current.scrollHeight;
            }
        };
        extendedRef.trim = () => {
            if (inputRef.current) {
                inputRef.current.value = inputRef.current.value.trim();
            }
        };
        extendedRef.element = () => inputRef.current;
        extendedRef.mwebFocusRoot = (opts) => inputRef.current?.focus(opts);
        return extendedRef;
    }, [focusAtEnd]);
    useEffect(() => {
        if (autoFocus && !isMobileUserAgent) {
            focusAtEnd();
        }
    }, [autoFocus, focusAtEnd, isMobileUserAgent]);
    const handleChange = useCallback((event) => {
        onChange?.(event.target.value);
    }, [onChange]);
    const handleKeyDown = useCallback((event) => {
        handleKeyDownInput(event.nativeEvent, {
            enterKeyBehavior: 'submit',
            isComposing,
            onKeyDown,
        });
    }, [onKeyDown, isComposing]);
    const labelErrorText = errorPlacement === InputErrorPlacement.LABEL ? errorText : undefined;
    return (_jsxs("div", { className: wrapperClassName, children: [_jsx(FormItemHeader, { label: label, subtitle: subtitle, isOptional: isOptional, errorText: labelErrorText, withMargin: labelMargin }), _jsx("div", { className: "rounded-full", children: _jsxs("div", { className: "relative flex items-center", children: [_jsx("input", { type: type, ref: inputRef, autoFocus: autoFocus, placeholder: placeholder, disabled: disabled, value: value, required: required, name: name ?? label, onChange: handleChange, onKeyDown: handleKeyDown, onCompositionStart: () => handleCompositionStart(setIsComposing), onCompositionEnd: () => handleCompositionEnd(setIsComposing), onBlur: handleBlur, onFocus: handleFocus, onPaste: onPaste, className: inputClass, autoComplete: AUTOCOMPLETE_TYPES.includes(type ?? '') ? type : 'off', "data-test-id": testId, "data-1p-ignore": disable1pass, enterKeyHint: enterKeyHint, min: min, max: max, step: step, spellCheck: spellCheck, maxLength: maxLength, "aria-label": ariaLabel ?? label }), leadingAccessoryComponent, rightAttribution] }) }), inlineEditBlock && _jsx("div", { className: "mt-2 flex justify-end", children: inlineEditBlock })] }));
});
export default Input;
//# sourceMappingURL=Input.js.map